import { clearAuthState } from "@/hooks/useAuth"; // adjust import if needed
import axios from "axios";
import { getAuthToken, getLoginType } from "@/utils/secureStorage";

// Use environment variable for backend URL
export const API_BASE_URL = import.meta.env.VITE_BACKEND_ENDPOINT || "https://api.production.gnzabe.com";
export const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // This is important for cookies
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Add request interceptor to handle CORS and auth
axiosInstance.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Don't override Content-Type for FormData (multipart/form-data)
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    // Log outgoing requests
    console.log("🌐 [HTTP_REQUEST]", {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL}${config.url}`,
      hasAuthHeader: !!config.headers.Authorization,
      authTokenLength: config.headers.Authorization?.length || 0,
      headers: config.headers,
      data: config.data
    });

    return config;
  },
  (error) => {
    console.error("❌ [HTTP_REQUEST_ERROR]", error);
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors
axiosInstance.interceptors.response.use(
  (response) => {
    // Log successful responses
    console.log("✅ [HTTP_RESPONSE]", {
      method: response.config.method?.toUpperCase(),
      url: response.config.url,
      status: response.status,
      statusText: response.statusText,
      data: response.data
    });
    return response;
  },
  (error) => {
    // Log error responses
    console.error("❌ [HTTP_RESPONSE_ERROR]", {
      method: error.config?.method?.toUpperCase(),
      url: error.config?.url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message
    });
    const url = error.config?.url || "";
    const isCompanyAdmin = getLoginType() === "company";
    const isCourseOrTutorial = url.includes("/api/v1/courses") || url.includes("/api/v1/tutorials");
    const isCourseStatus = url.includes("/api/v1/companies/course-status");
    const isProfileUpdate = url.includes("/api/v1/users/update-me") || url.includes("/api/v1/companies/update-me");
    const isTerminateUser = url.includes("/api/v1/users/terminate");

    if (error.response && error.response.status === 401) {
      // If company admin is trying to access courses or tutorials, do not redirect
      if (isCompanyAdmin && isCourseOrTutorial) {
        return Promise.reject(error);
      }
      // For all users, if the endpoint is course status, do not redirect/logout
      if (isCourseStatus) {
        return Promise.reject(error);
      }
      // For profile updates, do not redirect/logout - let the component handle the error
      if (isProfileUpdate) {
        return Promise.reject(error);
      }
      // For user termination, do not redirect/logout - let the component handle the error
      if (isTerminateUser) {
        return Promise.reject(error);
      }
      // For company data endpoints, do not redirect/logout - let the component handle the error
      if (
        url.includes("/api/v1/companies/get-current-company") ||
        url.includes("/api/v1/companies/")
      ) {
        return Promise.reject(error);
      }
      // For user data endpoint, do not redirect/logout - let the component handle the error
      if (url.includes("/api/v1/users/get-current-user")) {
        return Promise.reject(error);
      }
      // For departments endpoints, do not redirect/logout - let the component handle the error
      if (url.includes("/api/v1/departments")) {
        return Promise.reject(error);
      }
      // For courses endpoint, do not redirect/logout - let the component handle the error
      if (url.includes("/api/v1/courses")) {
        return Promise.reject(error);
      }
      // For all other 401s, clear auth and redirect
      if (typeof clearAuthState === "function") clearAuthState();
      window.location.href = "/login?sessionExpired=1";
      return;
    }
    return Promise.reject(error);
  }
);
